import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';


export default function Analytics() {
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [resultsFile, setResultsFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loggedInUser = JSON.parse(localStorage.getItem('user') || "{}");
  const isStudent = loggedInUser.role === 'student' || loggedInUser.role === 'Student';
  const filteredStudents = (Array.isArray(students) ? students : []).filter(s => s?.batchIdStr?.trim() === selectedBatch?.trim());

  const fetchBatches = () => {
    setLoadingBatches(true);
    const sessionUser = JSON.parse(localStorage.getItem('user') || "{}");
    const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
    axios.get(API_BASE_URL + '/api/batches?t=' + new Date().getTime(), { headers })
      .then(res => setBatches(Array.isArray(res.data) ? res.data : []))
      .catch(console.error)
      .finally(() => setLoadingBatches(false));
  };

  const fetchStudents = () => {
    setLoadingStudents(true);
    const sessionUser = JSON.parse(localStorage.getItem('user') || "{}");
    const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
    axios.get(API_BASE_URL + '/api/students?t=' + new Date().getTime(), { headers })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setStudents(data);
        if (isStudent && loggedInUser.email) {
            const me = data.find(s => s.email?.toLowerCase() === loggedInUser.email?.toLowerCase());
            if (me) {
                setSelectedStudent(me);
                setSelectedBatch(me.batchIdStr);
            }
        }
      })
      .catch(err => {
        console.error("Student analytics fetch failed:", err);
        toast.error("Roster synchronization failed. Performance metrics may be outdated.");
        setStudents([]);
      })
      .finally(() => setLoadingStudents(false));
  };

  useEffect(() => {
    fetchBatches();
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadResults = (e) => {
    e.preventDefault();
    if (!resultsFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', resultsFile);
    
    const sessionUser = JSON.parse(localStorage.getItem('user') || "{}");
    const headers = { 'X-Tenant-ID': sessionUser?.tenantEmail || sessionUser?.email || 'PENSENSE_DEFAULT' };
    
    axios.post(API_BASE_URL + '/api/students/results/upload', formData, { headers })
      .then(res => {
        toast.success(res.data?.message || "Successfully processed data!");
        setShowUploadModal(false);
        setResultsFile(null);
        fetchStudents(); // Refresh data immediately for table
        
        // Auto-reselect the student if they were selected so the new test score triggers a re-render
        if(selectedStudent) {
            axios.get(API_BASE_URL + '/api/students?t=' + Date.now(), { headers }).then(r => {
                const refreshed = (Array.isArray(r.data) ? r.data : []).find(s => s.id === selectedStudent.id);
                if(refreshed) setSelectedStudent(refreshed);
            });
        }
      })
      .catch(err => {
        const msg = err.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err.message;
        toast.error("Failed to upload Result Analytics: " + msg, { autoClose: false });
      })
      .finally(() => setUploading(false));
  };

  // Helper calculation
  const getAverageScore = (testResults) => {
      if(!testResults || !Array.isArray(testResults) || testResults.length === 0) return 0;
      let totalPerc = 0;
      testResults.forEach(r => {
          totalPerc += (r.marksObtained / r.totalMarks) * 100;
      });
      return Math.round(totalPerc / testResults.length);
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Welcome Banner */}
      <div className="relative rounded-3xl bg-gradient-to-r from-blue-900/40 via-indigo-900/20 to-slate-900 border border-indigo-500/20 p-8 overflow-hidden">
        {/* Glow Effects */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full"></div>
        <div className="absolute bottom-0 left-32 w-48 h-48 bg-blue-500/10 blur-[60px] rounded-full"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex-1 max-w-2xl">
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-400">
              Result Analytics Engine
            </h1>
            <p className="text-indigo-200/70 mb-6">Bulk upload grading sheets and drill down into individual student performance trajectories.</p>

            <div className="flex flex-col sm:flex-row gap-4">
              {!isStudent ? (
                <>
                  <select 
                    className="w-full md:w-72 bg-slate-900/80 border border-indigo-500/20 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-indigo-500 transition-colors shadow-inner disabled:opacity-50 disabled:cursor-wait"
                    value={selectedBatch}
                    onChange={e => {
                      setSelectedBatch(e.target.value);
                      setSelectedStudent(null);
                    }}
                    disabled={loadingBatches}
                  >
                    <option value="">
                      {loadingBatches 
                        ? "Loading Batches..." 
                        : batches.length === 0 
                          ? "No Batches Available" 
                          : "Select a Batch..."}
                    </option>
                    {batches.map(b => (
                      <option key={b.id} value={b.batchIdStr}>{b.name} ({b.batchIdStr})</option>
                    ))}
                  </select>
                  
                  <select 
                    className="w-full md:w-80 bg-slate-900/80 border border-indigo-500/20 rounded-xl px-4 py-3 text-slate-200 focus:outline-none focus:border-indigo-500 transition-colors shadow-inner font-medium disabled:opacity-50 disabled:cursor-wait"
                    value={selectedStudent?.id || ''}
                    onChange={e => setSelectedStudent(students.find(st => st.id.toString() === e.target.value) || null)}
                    disabled={!selectedBatch || loadingStudents}
                  >
                    <option value="">
                      {loadingBatches
                        ? "Loading Batches..."
                        : !selectedBatch
                          ? "Select a Batch first..."
                          : loadingStudents 
                            ? "Loading Students..." 
                            : filteredStudents.length === 0 
                              ? "No Students in This Batch" 
                              : "Select a Student..."}
                    </option>
                    {filteredStudents.map(st => (
                      <option key={st.id} value={st.id}>{st.name || "Unnamed"}</option>
                    ))}
                  </select>
                </>
              ) : (
                 <div className="bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 font-medium px-6 py-3 rounded-xl shadow-inner inline-flex items-center gap-2">
                   <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                   Viewing Personal Academic Record
                 </div>
              )}
            </div>
          </div>
          
          {!isStudent && (
            <div className="flex flex-col items-end gap-3">
               <button onClick={() => setShowUploadModal(true)} className="px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white font-medium shadow-lg shadow-indigo-500/20 transition-all flex items-center gap-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                  Upload Test Scores
               </button>
            </div>
          )}
        </div>
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 w-full max-w-md shadow-2xl relative animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start mb-2">
              <h2 className="text-xl font-bold text-white">Upload Test Results</h2>
              <a href="/Sample_Test_Results.csv" download className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1.5 bg-indigo-500/10 px-3 py-1.5 rounded-lg border border-indigo-500/20 hover:border-indigo-500/40 transition-all shadow-sm">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Sample Format
              </a>
            </div>
            <p className="text-slate-400 text-sm mb-6">Select a CSV or Excel file containing the grades.</p>
            
            <form onSubmit={handleUploadResults} className="space-y-6">
              <div className="relative group">
                <div className="absolute inset-0 bg-indigo-500/10 rounded-2xl border-2 border-dashed border-indigo-500/30 group-hover:border-indigo-500/50 transition-colors"></div>
                <input 
                  type="file" 
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={(e) => setResultsFile(e.target.files[0])}
                  className="relative z-10 w-full h-32 opacity-0 cursor-pointer"
                  required
                />
                <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center text-indigo-500">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mb-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                  <span className="font-medium text-sm">{resultsFile ? resultsFile.name : 'Click or drop file here'}</span>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowUploadModal(false)} className="flex-1 py-3 rounded-xl border border-slate-700 font-semibold text-slate-300 hover:bg-slate-800 transition-colors">Cancel</button>
                <button type="submit" disabled={uploading || !resultsFile} className="flex-[2] py-3 rounded-xl bg-indigo-500 font-semibold text-white hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/20">
                  {uploading ? 'Processing...' : 'Upload Data'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {!selectedStudent ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] border-2 border-dashed border-indigo-500/20 rounded-3xl bg-slate-900/20 mt-8 relative overflow-hidden">
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-500/10 blur-[100px] rounded-full"></div>
           <div className="relative z-10 flex flex-col items-center">
             <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center text-indigo-500/50 mb-6 shadow-inner ring-1 ring-white/5">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
             </div>
             <h3 className="text-2xl font-bold text-slate-300 mb-2">No Student Selected</h3>
             <p className="text-slate-500 text-center max-w-md">Choose a Batch and then drop down to select a specific enrolled student to view their academic test trajectory.</p>
           </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        
        {/* Left Stats Column */}
        <div className="lg:col-span-1 space-y-6">
           <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 relative overflow-hidden group">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M16 12A4 4 0 1 0 8 12M12 8v4M12 12l2 2"/></svg>
              </div>
              <p className="text-sm font-medium text-slate-400 mb-1">Global Accuracy Rate</p>
              <div className="flex items-end gap-2">
                <h3 className="text-3xl font-bold text-white">{getAverageScore(selectedStudent.testResults)}<span className="text-lg text-blue-400">%</span></h3>
              </div>
           </div>
           
           <div className="rounded-3xl border border-indigo-500/20 bg-slate-900/40 p-6 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-[40px] rounded-full"></div>
              <div className="w-12 h-12 rounded-xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline></svg>
              </div>
              <p className="text-sm font-medium text-slate-400 mb-1">Total Exams Taken</p>
              <div className="flex items-end gap-2">
                <h3 className="text-3xl font-bold text-white">{selectedStudent.testResults ? selectedStudent.testResults.length : 0} <span className="text-lg text-indigo-400">tests</span></h3>
              </div>
           </div>
        </div>

        {/* Right Table Column */}
        <div className="lg:col-span-3">
          <section className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 relative overflow-hidden h-full">
            <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/5 blur-[50px] rounded-full"></div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 relative z-10 border-b border-slate-800 pb-4">
              <h2 className="text-xl font-semibold text-slate-200 flex items-center gap-2">
                <span className="text-indigo-400"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg></span>
                Historical Examination Records
              </h2>
            </div>
            
            <div className="overflow-x-auto relative z-10">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 bg-slate-950/50 border border-slate-800 rounded-t-xl">
                    <th className="p-4 font-semibold rounded-tl-xl border-b border-slate-800">Date Logged</th>
                    <th className="p-4 font-semibold border-b border-slate-800">Test Document Name</th>
                    <th className="p-4 font-semibold border-b border-slate-800">Subject Breakdown</th>
                    <th className="p-4 font-semibold border-b border-slate-800">Score</th>
                    <th className="p-4 font-semibold border-b border-slate-800">Max Marks</th>
                    <th className="p-4 font-semibold rounded-tr-xl border-b border-slate-800 text-right">Result Percentage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {(!selectedStudent.testResults || selectedStudent.testResults.length === 0) ? (
                    <tr>
                      <td colSpan="6" className="p-12 text-center text-slate-500 font-medium tracking-wide">
                        No test records uploaded. Use the purple button above to sync Excel data.
                      </td>
                    </tr>
                  ) : (
                    selectedStudent.testResults.map((test, idx) => {
                      const percentage = Math.round((test.marksObtained / test.totalMarks) * 100);
                      
                      let badgeColor = "bg-rose-500/10 text-rose-400 border-rose-500/20";
                      if (percentage >= 80) badgeColor = "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
                      else if (percentage >= 60) badgeColor = "bg-amber-500/10 text-amber-400 border-amber-500/20";
                      
                      return (
                        <tr key={idx} className="hover:bg-slate-800/20 transition-colors">
                          <td className="p-4 text-sm font-mono text-slate-500">{test.date}</td>
                          <td className="p-4 text-sm font-medium text-slate-200">{test.testName}</td>
                          <td className="p-4 text-sm flex gap-2 flex-wrap items-center">
                            {test.physicsMax > 0 && <span className="px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[11px] font-semibold whitespace-nowrap">PHY: {test.physicsScore}/{test.physicsMax}</span>}
                            {test.chemMax > 0 && <span className="px-2 py-0.5 rounded-md bg-rose-500/10 text-rose-400 border border-rose-500/20 text-[11px] font-semibold whitespace-nowrap">CHM: {test.chemScore}/{test.chemMax}</span>}
                            {test.mathMax > 0 && <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[11px] font-semibold whitespace-nowrap">MTH: {test.mathScore}/{test.mathMax}</span>}
                            {test.bioMax > 0 && <span className="px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[11px] font-semibold whitespace-nowrap">BIO: {test.bioScore}/{test.bioMax}</span>}
                            {!test.physicsMax && !test.chemMax && !test.mathMax && !test.bioMax && <span className="text-slate-500 text-xs italic">Aggregate Only</span>}
                          </td>
                          <td className="p-4 text-sm font-bold text-indigo-400">{test.marksObtained}</td>
                          <td className="p-4 text-sm text-slate-400">{test.totalMarks}</td>
                          <td className="p-4 text-right">
                            <span className={`inline-block px-3 py-1 rounded-md text-[11px] font-bold tracking-wider border transition-colors ${badgeColor}`}>
                              {percentage}%
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
        
      </div>
      )}
    </div>
  );
}
